/**
 * Chat Interface Component
 * AI-powered conversational interface for vehicle queries
 * Enhanced with Knowledge Graph reasoning visualization
 * Multi-vehicle support (light commercial EV, city car, compressor)
 */

import React, { useRef, useEffect, useState } from 'react';
import useChatEngine from '../hooks/useChatEngine';
import { quickActionsByVehicle } from '../data/chatPatterns';
import KnowledgeGraphReasoning from './KnowledgeGraphReasoning';
import VoiceCallInterface from './VoiceCallInterface';
import VehicleSelector, { usePersistedVehicle } from './VehicleSelector';
import { PRODUCT, chatAssetName } from '../config/assets';
import { exportToPDF, exportConversationToPDF } from '../utils/knowledgeGraphAPI';
import './ChatInterface.css';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const LLM_MODEL = import.meta.env.VITE_OLLAMA_MODEL || 'gemma4:12b';

const ChatInterface = ({
  vehicleData, isOpen, onClose, isPrimaryMode = false,
  /** Called with (reasoningPath, message) when a new AI answer carries one. */
  onReasoningPath,
}) => {
  const [selectedVehicle, setSelectedVehicle] = usePersistedVehicle();

  const {
    messages,
    isLoading,
    inputValue,
    setInputValue,
    suggestions,
    sendMessage,
    sendQuickAction,
    clearChat,
    exportConversation
  } = useChatEngine(vehicleData, selectedVehicle);

  // Report each new AI answer's reasoning path upward exactly once, so the
  // dashboard can point the 3D model at what the answer was about. Keyed on the
  // message identity, not the array length, so re-renders never re-fire it.
  const reportedPathRef = useRef(null);
  useEffect(() => {
    if (typeof onReasoningPath !== 'function') return;
    const last = messages?.[messages.length - 1];
    if (!last || last.role !== 'ai') return;
    const path = last.reasoningPath;
    if (!Array.isArray(path) || path.length === 0) return;
    const key = last.id ?? `${messages.length}:${String(last.content).slice(0, 40)}`;
    if (reportedPathRef.current === key) return;
    reportedPathRef.current = key;
    onReasoningPath(path, last);
  }, [messages, onReasoningPath]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [showReasoningPanel, setShowReasoningPanel] = useState(false);
  const [currentReasoningPath, setCurrentReasoningPath] = useState(null);
  const [currentReasoningMsg, setCurrentReasoningMsg] = useState(null);
  const [expandedGraph, setExpandedGraph] = useState(false);
  const [expandedSource, setExpandedSource] = useState(null); // key of source to maximize
  const [expandedRetrieval, setExpandedRetrieval] = useState(false); // full retrieval overview
  const [showVoiceCall, setShowVoiceCall] = useState(false);
  const [prearmedAudio, setPrearmedAudio] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [zoomedImageIndex, setZoomedImageIndex] = useState(0);
  const [allImages, setAllImages] = useState([]);
  const [cvAnalysis, setCvAnalysis] = useState(null);
  const [cvAnalysisLoading, setCvAnalysisLoading] = useState(false);
  const [showCvAnalysis, setShowCvAnalysis] = useState(false);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Handle ESC key to close zoom modal (only if modal is open)
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && zoomedImage) {
        e.preventDefault();
        e.stopPropagation();
        setZoomedImage(null);
      }
    };

    if (zoomedImage) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [zoomedImage]);

  // Handle arrow keys for Previous/Next navigation in zoom modal
  useEffect(() => {
    const handleArrowKeys = (e) => {
      if (!zoomedImage || allImages.length === 0) return;

      if (e.key === 'ArrowLeft' && zoomedImageIndex > 0) {
        e.preventDefault();
        const newIndex = zoomedImageIndex - 1;
        setZoomedImageIndex(newIndex);
        setZoomedImage(allImages[newIndex]);
      } else if (e.key === 'ArrowRight' && zoomedImageIndex < allImages.length - 1) {
        e.preventDefault();
        const newIndex = zoomedImageIndex + 1;
        setZoomedImageIndex(newIndex);
        setZoomedImage(allImages[newIndex]);
      }
    };

    if (zoomedImage) {
      window.addEventListener('keydown', handleArrowKeys);
      return () => window.removeEventListener('keydown', handleArrowKeys);
    }
  }, [zoomedImage, zoomedImageIndex, allImages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      sendMessage(inputValue);
    }
  };

  const handleQuickAction = (query) => {
    sendQuickAction(query);
  };

  const handleExport = () => {
    const conversation = exportConversation();
    const blob = new Blob([conversation], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vehicle-chat-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openReasoningPanel = (msg) => {
    setCurrentReasoningPath(msg.reasoningPath);
    setCurrentReasoningMsg(msg);
    setShowReasoningPanel(true);
  };

  const closeReasoningPanel = () => {
    setShowReasoningPanel(false);
    setCurrentReasoningPath(null);
    setCurrentReasoningMsg(null);
  };

  const handleFeedback = async (msg, rating) => {
    // Update message state
    setMessages(prev => prev.map(m =>
      m.id === msg.id ? { ...m, feedback: rating } : m
    ));
    // Send to backend
    try {
      await fetch(`${API_BASE}/api/v1/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: messages.find(m => m.role === 'user' && m.id < msg.id)?.message || '',
          answer: msg.message?.slice(0, 500) || '',
          rating,
          confidence: msg.confidence || 0,
          intent: msg.intent || '',
          retrieval_method: msg.isHybrid ? 'hybrid_rag' : 'traditional_kg',
        }),
      });
    } catch (e) {
      console.error('Feedback submission failed:', e);
    }
  };

  const handleFlagForReview = async (msg) => {
    setMessages(prev => prev.map(m =>
      m.id === msg.id ? { ...m, flagged: true } : m
    ));
    try {
      await fetch(`${API_BASE}/api/v1/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: messages.find(m => m.role === 'user' && m.id < msg.id)?.message || '',
          answer: msg.message?.slice(0, 500) || '',
          confidence: msg.confidence || 0,
          reason: 'low_confidence',
        }),
      });
    } catch (e) {
      console.error('Review flag failed:', e);
    }
  };

  const handlePDFExport = async (msg) => {
    try {
      console.log('[ChatInterface] Exporting message to PDF:', msg.id);

      // Prepare response object for PDF export
      const response = {
        answer: msg.message,
        visual_content: {
          images: msg.images || [],
          tables: msg.tables || [],
        },
        confidence: msg.confidence || 0,
        citations: Array.isArray(msg.citations) ? msg.citations : (msg.citations ? [{text: msg.citations}] : []),
        reasoning_path: msg.reasoningPath || [],
      };

      const vehicleName = chatAssetName(selectedVehicle);

      // Get original question (find previous user message)
      const msgIndex = messages.findIndex(m => m.id === msg.id);
      const userMsg = msgIndex > 0 ? messages[msgIndex - 1] : null;
      const query = userMsg && userMsg.role === 'user' ? userMsg.message : 'Query';

      // Call PDF export API
      const pdfBlob = await exportToPDF(query, response, vehicleName);

      // Download PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `diagnostic-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      console.log('[ChatInterface] ✅ PDF downloaded successfully');
    } catch (error) {
      console.error('[ChatInterface] PDF export failed:', error);
      alert('PDF export failed. Please try again.');
    }
  };

  const handleFullConversationPDFExport = async () => {
    try {
      console.log('[ChatInterface] Exporting full conversation to PDF');

      const vehicleName = chatAssetName(selectedVehicle);

      // Prepare conversation history for export (all messages with metadata)
      const conversationForExport = messages.map(msg => ({
        role: msg.role,
        message: msg.message,
        timestamp: msg.timestamp?.toISOString?.() || new Date().toISOString(),
        confidence: msg.confidence,
        citations: msg.citations,
        images: msg.images,
        tables: msg.tables,
      }));

      console.log('[ChatInterface] Exporting', conversationForExport.length, 'messages');

      // Call PDF export API
      const pdfBlob = await exportConversationToPDF(conversationForExport, vehicleName);

      // Download PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversation-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      console.log('[ChatInterface] ✅ Full conversation PDF downloaded successfully');
    } catch (error) {
      console.error('[ChatInterface] Full conversation PDF export failed:', error);
      alert('Full conversation PDF export failed. Please try again.');
    }
  };

  // Handle CV analysis of image
  const handleCVAnalysis = async (image) => {
    try {
      console.log('[ChatInterface] Analyzing image with computer vision:', image.file_path);
      setCvAnalysisLoading(true);
      setCvAnalysis(null);
      setShowCvAnalysis(true);

      // Call CV analysis API
      const response = await fetch(`${API_BASE}/analyze-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_path: image.file_path,
          analysis_types: ['all'], // OCR, vision, and technical analysis
        }),
      });

      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[ChatInterface] ✅ CV analysis complete:', result);
      setCvAnalysis(result);
    } catch (error) {
      console.error('[ChatInterface] CV analysis failed:', error);
      setCvAnalysis({
        error: error.message || 'Analysis failed. Please try again.',
      });
    } finally {
      setCvAnalysisLoading(false);
    }
  };

  // Vehicle-specific content configuration
  const vehicleConfig = {
    nexon: {
      title: "Light commercial EV manual assistant",
      subtitle: "AI-powered knowledge graph search",
      icon: "📖",
      welcomeTitle: "Light commercial EV manual assistant",
      examples: [
        "Battery specifications and capacity",
        "Component information and locations",
        "Safety warnings and procedures",
        "Maintenance guidelines",
        "Technical specifications"
      ]
    },
    nano: {
      title: "City car manual assistant",
      subtitle: "Support & Maintenance Manual Search",
      icon: "🔧",
      welcomeTitle: "City car support & maintenance manual",
      examples: [
        "ECU specifications and wiring diagrams",
        "Sensor locations and information",
        "Engine specifications and details",
        "Electrical system and relays",
        "Component troubleshooting"
      ]
    },
    compressor: {
      title: "Rotary screw compressor manual assistant",
      subtitle: "Industrial Compressor Technical Manual",
      icon: "⚙️",
      welcomeTitle: "Rotary screw compressor",
      examples: [
        "Safety precautions and lockout procedures",
        "Maintenance schedule and oil changes",
        "Troubleshooting and diagnostic codes",
        "Operating instructions and controls",
        "Technical specifications and data"
      ]
    }
  };

  const currentConfig = vehicleConfig[selectedVehicle] || vehicleConfig.nexon;
  const currentQuickActions = quickActionsByVehicle[selectedVehicle] || quickActionsByVehicle.nexon;

  if (!isOpen) return null;

  return (
    <div className={`chat-interface-overlay ${isPrimaryMode ? 'primary-mode' : ''}`}>
      <div className={`chat-interface glass-panel ${isPrimaryMode ? 'primary-mode' : ''}`}>
        {/* Header */}
        <div className="chat-header">
          <div className="chat-header-left">
            <span className="chat-icon">{currentConfig.icon}</span>
            <div className="chat-header-info">
              <h3>{currentConfig.title}</h3>
              <p className="chat-subtitle">{currentConfig.subtitle}</p>
            </div>
          </div>

          {/* Vehicle Selector */}
          <VehicleSelector
            selectedVehicle={selectedVehicle}
            onVehicleChange={setSelectedVehicle}
          />

          <div className="chat-header-actions">
            {/* Voice Call Button */}
            <button
              className="chat-action-btn voice-call-btn"
              onClick={async () => {
                // Browsers (Chrome/Safari on HTTPS, iOS in particular) require
                // AudioContext.resume() and getUserMedia() to be invoked from
                // a real user-gesture frame. Doing it inside ws.onopen later
                // leaves the AudioContext suspended → analyser returns zeros
                // → VAD never triggers → no audio is ever sent.
                let ctx = null, stream = null;
                try {
                  ctx = new (window.AudioContext || window.webkitAudioContext)();
                  if (ctx.state === 'suspended') await ctx.resume();
                  stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                      channelCount: 1,
                      echoCancellation: true,
                      noiseSuppression: true,
                      autoGainControl: true,
                    },
                  });
                  console.log('[VoiceCall] Pre-armed: ctx.state=', ctx.state,
                              'sampleRate=', ctx.sampleRate, 'tracks=', stream.getAudioTracks().length);
                  setPrearmedAudio({ ctx, stream });
                } catch (err) {
                  console.error('[VoiceCall] Pre-arm failed:', err);
                  if (stream) stream.getTracks().forEach(t => t.stop());
                  if (ctx && ctx.state !== 'closed') { try { ctx.close(); } catch {} }
                  setPrearmedAudio(null);
                  alert('Could not access the microphone. Please allow mic access and try again.');
                  return;
                }
                setShowVoiceCall(true);
              }}
              title={`Start voice call with ${PRODUCT.assistant}`}
            >
              <span className="voice-call-indicator">🔴</span>
            </button>
            {messages.length > 0 && (
              <>
                <button
                  className="chat-action-btn"
                  onClick={handleExport}
                  title="Export conversation (Text)"
                >
                  📥
                </button>
                <button
                  className="chat-action-btn pdf-export-btn"
                  onClick={handleFullConversationPDFExport}
                  title="Export Full Conversation to PDF"
                >
                  📄
                </button>
                <button
                  className="chat-action-btn"
                  onClick={clearChat}
                  title="Clear chat"
                >
                  🗑️
                </button>
              </>
            )}
            <button
              className="chat-close-btn"
              onClick={onClose}
              title="Close chat"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="chat-messages">
          {messages.length === 0 ? (
            <div className="chat-welcome">
              <div className="welcome-icon">{currentConfig.icon}</div>
              <h4>{currentConfig.welcomeTitle}</h4>
              <p>Ask me anything from the {selectedVehicle === 'nano' ? 'support & maintenance manual' : "owner's manual"}:</p>
              <ul className="welcome-examples">
                {currentConfig.examples.map((example, idx) => (
                  <li key={idx}>{example}</li>
                ))}
              </ul>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`chat-message ${msg.role === 'user' ? 'chat-message-user' : 'chat-message-ai'}`}
              >
                <div className="message-avatar">
                  {msg.role === 'user' ? '👤' : '🤖'}
                </div>
                <div className="message-content">
                  <div className="message-text">
                    {msg.message.split('\n').map((line, idx) => {
                      // Parse markdown-style bold text
                      const parts = line.split(/(\*\*.*?\*\*)/g);
                      return (
                        <p key={idx}>
                          {parts.map((part, i) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              return <strong key={i}>{part.slice(2, -2)}</strong>;
                            }
                            return <span key={i}>{part}</span>;
                          })}
                        </p>
                      );
                    })}
                  </div>

                  {/* Multimodal Content: Images */}
                  {msg.images && msg.images.length > 0 && msg.role === 'ai' && (
                    <div className="message-images">
                      <div className="images-header">
                        <span className="images-icon">🖼️</span>
                        <span className="images-label">Related Images ({msg.images.length})</span>
                      </div>
                      <div className="images-grid">
                        {msg.images.map((img, imgIdx) => {
                          console.log('[ChatInterface] Rendering image:', img);
                          return (
                            <div
                              key={imgIdx}
                              className="image-card"
                              onClick={() => {
                                setZoomedImage(img);
                                setZoomedImageIndex(imgIdx);
                                setAllImages(msg.images);
                              }}
                              title="Click to zoom"
                            >
                              <div className="image-wrapper">
                                <img
                                  src={`${API_BASE}/${img.file_path}`}
                                  alt={img.description || `Image from page ${img.page}`}
                                  className="message-image"
                                  onLoad={() => console.log(`[ChatInterface] ✅ Image ${imgIdx + 1} loaded successfully`)}
                                  onError={(e) => {
                                    console.error(`[ChatInterface] ❌ Image ${imgIdx + 1} failed to load:`, img.file_path);
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150"%3E%3Crect fill="%23333" width="200" height="150"/%3E%3Ctext x="50%25" y="50%25" fill="%23999" text-anchor="middle" dy=".3em"%3EImage unavailable%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                                <div className="image-zoom-overlay">
                                  <span className="zoom-icon">🔍</span>
                                </div>
                              </div>
                              <div className="image-info">
                                <span className="image-page">Page {img.page}</span>
                                {img.description && (
                                  <span className="image-description">{img.description}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Multimodal Content: Tables */}
                  {msg.tables && msg.tables.length > 0 && msg.role === 'ai' && (
                    <div className="message-tables">
                      <div className="tables-header">
                        <span className="tables-icon">📊</span>
                        <span className="tables-label">Specification Tables ({msg.tables.length})</span>
                      </div>
                      {msg.tables.map((table, tableIdx) => {
                        console.log('[ChatInterface] Rendering table:', table);
                        return (
                          <div key={tableIdx} className="table-card">
                            {table.caption && (
                              <div className="table-caption">{table.caption}</div>
                            )}
                            <div className="table-wrapper">
                              <table className="spec-table">
                                {table.headers && (
                                  <thead>
                                    <tr>
                                      {table.headers.map((header, hIdx) => (
                                        <th key={hIdx}>{header}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                )}
                                <tbody>
                                  {table.rows && table.rows.map((row, rIdx) => (
                                    <tr key={rIdx}>
                                      {row.map((cell, cIdx) => (
                                        <td key={cIdx}>{cell}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div className="table-info">
                              <span className="table-page">Page {table.page}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Knowledge Graph metadata: confidence and citations */}
                  {msg.confidence && msg.role === 'ai' && (
                    <div className="message-metadata">
                      {/* Hybrid Mode Badge */}
                      {msg.isHybrid && (
                        <span className="hybrid-badge" title="Hybrid RAG: Vector + BM25 + Knowledge Graph">
                          🔬 Hybrid
                        </span>
                      )}
                      <span className={`confidence-badge ${msg.confidence > 0.8 ? 'high' : 'medium'}`}>
                        {Math.round(msg.confidence * 100)}% confident
                      </span>
                      {msg.citations && Array.isArray(msg.citations) && msg.citations.length > 0 && (
                        <span className="citations" title={`Found in ${msg.citations.length} location${msg.citations.length > 1 ? 's' : ''}`}>
                          📄 Reference Page{msg.citations.length > 1 ? 's' : ''}: {
                            Array.isArray(msg.citations)
                              ? msg.citations
                                  .slice(0, 5)  // Show first 5 pages
                                  .map(c => c.page || c)
                                  .filter(p => p)  // Filter out null/undefined pages
                                  .join(', ')
                              : ''
                          }
                          {msg.citations.length > 5 && ` (+${msg.citations.length - 5} more)`}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Unified Reasoning Summary — compact inline + side panel trigger */}
                  {msg.role === 'ai' && (msg.reasoningPath?.length > 0 || msg.retrievalDetails?.sources) && (
                    <div className="reasoning-inline-strip" onClick={() => openReasoningPanel(msg)}>
                      <div className="reasoning-strip-left">
                        <span className="reasoning-strip-icon">🧠</span>
                        <div className="reasoning-strip-info">
                          <span className="reasoning-strip-label">
                            {msg.isHybrid ? 'Hybrid RAG' : 'Knowledge Graph'}
                          </span>
                          <span className="reasoning-strip-details">
                            {(() => {
                              const sources = msg.retrievalDetails?.sources || {};
                              const parts = [];
                              if (sources.vector_store?.count > 0) parts.push(`🔍 ${sources.vector_store.count}`);
                              if (sources.bm25?.count > 0) parts.push(`📝 ${sources.bm25.count}`);
                              if (sources.knowledge_graph?.count > 0) parts.push(`🕸️ ${sources.knowledge_graph.count}`);
                              if (parts.length === 0 && msg.reasoningPath?.length > 0) parts.push(`🕸️ ${msg.reasoningPath.length} nodes`);
                              return parts.join('  ·  ') || 'View details';
                            })()}
                          </span>
                        </div>
                      </div>
                      {/* Mini weight bar */}
                      {msg.retrievalDetails?.ensemble_weights && (
                        <div className="reasoning-strip-weights">
                          {Object.entries(msg.retrievalDetails.ensemble_weights).map(([source, weight]) => (
                            <div
                              key={source}
                              className={`reasoning-strip-weight-seg weight-${source.replace('_', '-')}`}
                              style={{ width: `${weight * 100}%` }}
                              title={`${source}: ${(weight * 100).toFixed(0)}%`}
                            />
                          ))}
                        </div>
                      )}
                      <span className="reasoning-strip-expand">View details →</span>
                    </div>
                  )}

                  {/* Feedback + Actions Row */}
                  {msg.role === 'ai' && (
                    <div className="message-actions-row">
                      {/* Feedback Buttons */}
                      <div className="feedback-buttons">
                        <button
                          className={`feedback-btn feedback-up ${msg.feedback === 'up' ? 'active' : ''}`}
                          onClick={() => handleFeedback(msg, 'up')}
                          title="Good answer"
                          disabled={!!msg.feedback}
                        >
                          👍
                        </button>
                        <button
                          className={`feedback-btn feedback-down ${msg.feedback === 'down' ? 'active' : ''}`}
                          onClick={() => handleFeedback(msg, 'down')}
                          title="Bad answer"
                          disabled={!!msg.feedback}
                        >
                          👎
                        </button>
                        {msg.feedback && (
                          <span className="feedback-thanks">Thanks!</span>
                        )}
                      </div>

                      {/* Low Confidence Warning */}
                      {msg.confidence && msg.confidence < 0.6 && !msg.flagged && (
                        <button
                          className="flag-review-btn"
                          onClick={() => handleFlagForReview(msg)}
                          title="Flag this answer for expert review"
                        >
                          ⚠️ Low confidence — Flag for review
                        </button>
                      )}
                      {msg.flagged && (
                        <span className="flagged-label">📋 Flagged for review</span>
                      )}

                      {/* PDF Export */}
                      {msg.hasVisualContent && (
                        <button
                          className="pdf-export-btn"
                          onClick={() => handlePDFExport(msg)}
                          title="Export to PDF"
                        >
                          📄 PDF
                        </button>
                      )}
                    </div>
                  )}

                  <div className="message-time">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))
          )}
          {isLoading && (
            <div className="chat-message chat-message-ai">
              <div className="message-avatar">🤖</div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        {messages.length === 0 && (
          <div className="chat-quick-actions">
            <p className="quick-actions-label">Quick Actions:</p>
            <div className="quick-actions-grid">
              {currentQuickActions.map((action) => (
                <button
                  key={action.id}
                  className="quick-action-btn"
                  onClick={() => handleQuickAction(action.query)}
                  disabled={isLoading}
                >
                  <span className="quick-action-icon">{action.icon}</span>
                  <span className="quick-action-label">{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Smart Suggestions */}
        {suggestions.length > 0 && messages.length > 0 && (
          <div className="chat-suggestions">
            <p className="suggestions-label">💡 Suggestions:</p>
            <div className="suggestions-list">
              {suggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  className="suggestion-btn"
                  onClick={() => sendMessage(suggestion.replace(/.*Ask me /, ''))}
                  disabled={isLoading}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <form className="chat-input-form" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            className="chat-input"
            placeholder="Ask about specifications, components, safety, procedures..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            disabled={isLoading}
          />
          <button
            type="submit"
            className="chat-send-btn"
            disabled={!inputValue.trim() || isLoading}
            title="Send message"
          >
            {isLoading ? '⏳' : '📤'}
          </button>
        </form>
      </div>

      {/* Reasoning Side Panel — Slide-in from right */}
      <div className={`reasoning-side-panel ${showReasoningPanel ? 'open' : ''}`}>
        <div className="reasoning-panel-header">
          <h3>🧠 How I Found This Answer</h3>
          <button className="reasoning-panel-close" onClick={closeReasoningPanel}>✕</button>
        </div>

        <div className="reasoning-panel-body">
          {/* STAGE 1: Understanding the Question */}
          <div className="reasoning-stage">
            <div className="reasoning-stage-header">
              <span className="stage-number">1</span>
              <span className="stage-title">Understanding Your Question</span>
            </div>
            <div className="reasoning-stage-content">
              <div className="stage-field">
                <span className="stage-field-label">Query</span>
                <span className="stage-field-value">
                  "{currentReasoningMsg?.originalQuestion || messages.filter(m => m.role === 'user').slice(-1)[0]?.message || '...'}"
                </span>
              </div>
              {currentReasoningMsg?.intent && (
                <div className="stage-field">
                  <span className="stage-field-label">Detected Intent</span>
                  <span className="stage-intent-badge">{currentReasoningMsg.intent}</span>
                </div>
              )}
              {currentReasoningMsg?.retrievalDetails?.query_translations?.intent && (
                <div className="stage-field">
                  <span className="stage-field-label">Detected Intent</span>
                  <span className="stage-intent-badge">{currentReasoningMsg.retrievalDetails.query_translations.intent}</span>
                </div>
              )}
            </div>
          </div>

          {/* STAGE 2: Searching Sources — always show all 3 */}
          <div className="reasoning-stage">
            <div className="reasoning-stage-header">
              <span className="stage-number">2</span>
              <span className="stage-title">Retrieval Sources</span>
              <button
                className="stage-expand-btn"
                onClick={() => setExpandedRetrieval(true)}
                title="Expand full retrieval reasoning"
              >
                ⛶ Expand
              </button>
            </div>
            <div className="reasoning-stage-content">
              {(() => {
                const isHybrid = currentReasoningMsg?.isHybrid;
                const sources = currentReasoningMsg?.retrievalDetails?.sources || {};
                const weights = currentReasoningMsg?.retrievalDetails?.ensemble_weights || {};
                const kgNodes = currentReasoningPath?.length || 0;

                const sourceConfig = [
                  {
                    key: 'vector_store',
                    icon: '🔍',
                    name: 'ChromaDB — Semantic Search',
                    desc: 'Embeds query with all-MiniLM-L6-v2 and finds similar document chunks via cosine similarity',
                    active: isHybrid,
                    count: sources.vector_store?.count,
                    weight: weights.vector_store,
                  },
                  {
                    key: 'bm25',
                    icon: '📝',
                    name: 'BM25 — Keyword Match',
                    desc: 'Ranks documents by exact term frequency and inverse document frequency (TF-IDF)',
                    active: isHybrid,
                    count: sources.bm25?.count,
                    weight: weights.bm25,
                  },
                  {
                    key: 'knowledge_graph',
                    icon: '🕸️',
                    name: 'NetworkX — Graph Traversal',
                    desc: 'Translates query to graph operations via spaCy NLP, traverses nodes and relationships',
                    active: true,
                    count: sources.knowledge_graph?.count || kgNodes,
                    weight: isHybrid ? weights.knowledge_graph : 1.0,
                  },
                ];

                return (
                  <>
                    <div className="source-cards">
                      {sourceConfig.map((src) => (
                        <details
                          key={src.key}
                          className={`source-card source-card-${src.key.replace('_', '-')} ${!src.active ? 'source-card-inactive' : ''}`}
                          open={src.active}
                        >
                          <summary className="source-card-summary">
                            <div className="source-card-icon">{src.icon}</div>
                            <div className="source-card-info">
                              <div className="source-card-name">
                                {src.name}
                                {!src.active && <span className="source-inactive-label">(not active)</span>}
                              </div>
                              <div className="source-card-meta">
                                {src.active && src.count != null && (
                                  <span className="source-card-count">
                                    {src.count} {src.key === 'knowledge_graph' ? 'nodes' : 'docs'}
                                  </span>
                                )}
                                {src.active && src.weight != null && (
                                  <span className="source-card-weight">
                                    {(src.weight * 100).toFixed(0)}% weight
                                  </span>
                                )}
                              </div>
                            </div>
                            {src.active && (
                              <button
                                className="source-maximize-btn"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpandedSource(src.key); }}
                                title="Expand to full view"
                              >
                                ⛶
                              </button>
                            )}
                          </summary>
                          <div className="source-card-detail">
                            <div className="source-card-description">{src.desc}</div>

                            {/* Technical details */}
                            {src.active && sources[src.key]?.tech && (
                              <div className="source-tech-info">
                                {Object.entries(sources[src.key].tech).map(([k, v]) => (
                                  <div key={k} className="source-tech-row">
                                    <span className="source-tech-key">{k.replace(/_/g, ' ')}</span>
                                    <span className="source-tech-value">{String(v)}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Show retrieved document snippets */}
                            {src.active && sources[src.key]?.documents?.length > 0 && (
                              <div className="source-snippets">
                                <div className="source-snippets-label">Retrieved Chunks:</div>
                                {sources[src.key].documents.slice(0, 3).map((doc, idx) => {
                                  const content = typeof doc === 'string' ? doc
                                    : doc?.content || doc?.page_content || doc?.snippet || doc?.text || '';
                                  const page = typeof doc === 'object' ? doc?.page : null;
                                  const section = typeof doc === 'object' ? doc?.section : null;
                                  const nodeType = typeof doc === 'object' ? doc?.node_type : null;
                                  return (
                                    <div key={idx} className="source-snippet-item">
                                      <div className="snippet-header">
                                        <span className="snippet-index">#{idx + 1}</span>
                                        {page && <span className="snippet-page">pg.{page}</span>}
                                        {nodeType && <span className="snippet-type">{nodeType}</span>}
                                      </div>
                                      {section && <div className="snippet-section">{section.slice(0, 80)}{section.length > 80 ? '...' : ''}</div>}
                                      <div className="snippet-text">{content.slice(0, 200)}{content.length > 200 ? '...' : ''}</div>
                                    </div>
                                  );
                                })}
                                {sources[src.key].documents.length > 3 && (
                                  <div className="source-snippet-more">
                                    +{sources[src.key].documents.length - 3} more documents
                                  </div>
                                )}
                              </div>
                            )}

                            {/* KG-specific: reasoning nodes */}
                            {src.key === 'knowledge_graph' && sources[src.key]?.reasoning_nodes > 0 && (
                              <div className="source-kg-nodes">
                                Graph traversal: {sources[src.key].reasoning_nodes} reasoning nodes explored
                              </div>
                            )}
                          </div>
                        </details>
                      ))}
                    </div>
                    {isHybrid && (
                      <div className="fusion-label">
                        ↓ Reciprocal Rank Fusion (RRF) — merges results ↓
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* STAGE 3: Graph Traversal Visualization */}
          {currentReasoningPath && currentReasoningPath.length > 0 && (
            <div className="reasoning-stage">
              <div className="reasoning-stage-header">
                <span className="stage-number">3</span>
                <span className="stage-title">Knowledge Graph Traversal</span>
                <button
                  className="stage-expand-btn"
                  onClick={() => setExpandedGraph(true)}
                  title="Expand to full view"
                >
                  ⛶ Expand
                </button>
              </div>
              <div className="reasoning-stage-content reasoning-graph-container">
                <div className="kg-preview-summary">
                  {currentReasoningPath.length} nodes traversed:
                  {' '}
                  {currentReasoningPath.slice(0, 5).map((n, i) => (
                    <span key={i} className="kg-preview-node" style={{ color: n.node_type === 'Vehicle' ? '#9b59b6' : n.node_type === 'System' ? '#3498db' : n.node_type === 'Component' ? '#66B4C4' : '#00ff88' }}>
                      {n.node_data?.name || n.node_type}
                      {i < Math.min(4, currentReasoningPath.length - 1) ? ' → ' : ''}
                    </span>
                  ))}
                  {currentReasoningPath.length > 5 && <span style={{ color: '#6a7394' }}> +{currentReasoningPath.length - 5} more</span>}
                </div>
                <KnowledgeGraphReasoning
                  reasoningPath={currentReasoningPath}
                  isVisible={true}
                />
              </div>
            </div>
          )}

          {/* STAGE 4: LLM Reasoning */}
          <div className="reasoning-stage">
            <div className="reasoning-stage-header">
              <span className="stage-number">4</span>
              <span className="stage-title">LLM Reasoning</span>
            </div>
            <div className="reasoning-stage-content">
              <div className="stage-field">
                <span className="stage-field-label">Model</span>
                <span className="stage-field-value">Ollama — {LLM_MODEL} (local)</span>
              </div>
              {currentReasoningMsg?.confidence && (
                <div className="stage-field">
                  <span className="stage-field-label">Confidence</span>
                  <div className="confidence-bar-container">
                    <div
                      className="confidence-bar-fill"
                      style={{ width: `${Math.round(currentReasoningMsg.confidence * 100)}%` }}
                    />
                    <span className="confidence-bar-label">
                      {Math.round(currentReasoningMsg.confidence * 100)}%
                    </span>
                  </div>
                </div>
              )}
              {currentReasoningMsg?.citations && currentReasoningMsg.citations.length > 0 && (
                <div className="stage-field">
                  <span className="stage-field-label">Sources Used</span>
                  <span className="stage-field-value">
                    {currentReasoningMsg.citations.length} pages referenced
                  </span>
                </div>
              )}
              <div className="stage-field">
                <span className="stage-field-label">Mode</span>
                <span className="stage-field-value">
                  {currentReasoningMsg?.isHybrid ? '🔬 Hybrid RAG (Vector + BM25 + KG)' : '🕸️ Knowledge Graph + LLM'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Retrieval Reasoning — Full overview of all sources */}
      {expandedRetrieval && currentReasoningMsg && (() => {
        const isHybrid = currentReasoningMsg?.isHybrid;
        const details = currentReasoningMsg?.retrievalDetails || {};
        const sources = details.sources || {};
        const weights = details.ensemble_weights || {};
        const translations = details.query_translations || {};
        const kgNodes = currentReasoningPath?.length || 0;
        const totalDocs = details.total_retrieved || 0;
        const confidence = currentReasoningMsg?.confidence || 0;
        const citations = currentReasoningMsg?.citations || [];

        const allSources = [
          { key: 'vector_store', icon: '🔍', name: 'ChromaDB — Semantic Vector Search', color: '#66B4C4',
            howItWorks: [
              'Your question is converted into a 384-dimensional embedding vector using the all-MiniLM-L6-v2 model',
              'This vector is compared against all pre-indexed document chunk embeddings in ChromaDB',
              'The top-k most similar chunks are retrieved using cosine similarity scoring',
              'Results capture semantic meaning — synonyms and paraphrases still match even without exact keywords',
            ],
            whyItMatters: 'Finds conceptually relevant content even when the user\'s wording differs from the source text',
          },
          { key: 'bm25', icon: '📝', name: 'BM25 — Sparse Keyword Retrieval', color: '#ffaa32',
            howItWorks: [
              'Tokenizes your question into individual keywords (lowercased, whitespace-split)',
              'Scores every document using Okapi BM25 formula: TF × IDF × length normalization',
              'Term Frequency (TF): how often the keyword appears in a document',
              'Inverse Document Frequency (IDF): rare keywords get higher scores than common ones',
              'Documents with exact keyword matches rank highest',
            ],
            whyItMatters: 'Catches exact technical terms, model numbers, and specific names that embedding models might miss',
          },
          { key: 'knowledge_graph', icon: '🕸️', name: 'NetworkX — Multi-hop Graph Traversal', color: '#a050ff',
            howItWorks: [
              'spaCy NLP extracts entities and intent from your question (e.g., "charger" → Component entity)',
              'Translates to a graph query: find nodes matching the entity, then traverse relationships',
              'Multi-hop traversal follows edges: Vehicle → System → Component → Specification',
              'Each hop expands context by following HAS_SYSTEM, HAS_COMPONENT, HAS_SPEC relationships',
              'Aggregates all visited node data into a structured context for the LLM',
            ],
            whyItMatters: 'Provides structured, relationship-aware context that flat document search cannot — understands how components connect to systems',
          },
        ];

        return (
          <div className="retrieval-expanded-overlay" onClick={() => setExpandedRetrieval(false)}>
            <div className="retrieval-expanded-container" onClick={(e) => e.stopPropagation()}>
              <div className="retrieval-expanded-header">
                <div>
                  <h2>🔬 Retrieval Reasoning — Why This Answer?</h2>
                  <p className="retrieval-expanded-subtitle">
                    {isHybrid
                      ? `Hybrid RAG combined ${totalDocs} documents from 3 retrieval methods`
                      : `Knowledge Graph traversed ${kgNodes} nodes to build context`
                    }
                  </p>
                </div>
                <button className="source-expanded-close" onClick={() => setExpandedRetrieval(false)}>✕</button>
              </div>

              <div className="retrieval-expanded-body">
                {/* Query Understanding */}
                <div className="retrieval-expanded-section">
                  <h4>1. Query Understanding</h4>
                  <div className="retrieval-query-box">
                    <div className="retrieval-query-original">
                      <span className="retrieval-query-label">Your Question</span>
                      <span className="retrieval-query-text">"{translations.original || '...'}"</span>
                    </div>
                    <div className="retrieval-query-arrow">↓ spaCy NLP ↓</div>
                    <div className="retrieval-query-parsed">
                      {translations.intent && (
                        <div className="retrieval-parsed-item">
                          <span className="retrieval-parsed-label">Intent</span>
                          <span className="stage-intent-badge">{translations.intent}</span>
                        </div>
                      )}
                      {translations.cypher && (
                        <div className="retrieval-parsed-item">
                          <span className="retrieval-parsed-label">Graph Query</span>
                          <code className="retrieval-parsed-code">{translations.cypher}</code>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Retrieval Pipeline & Graph Traversal (Combined) */}
                <div className="retrieval-expanded-section">
                  <h4>2. Retrieval & Graph Traversal</h4>

                  {/* Weight bar */}
                  {isHybrid && (
                    <div className="retrieval-weight-bar-full">
                      {allSources.map((src) => {
                        const w = weights[src.key] || 0;
                        return (
                          <div
                            key={src.key}
                            className="retrieval-weight-segment"
                            style={{ width: `${w * 100}%`, background: src.color + '50', borderRight: '2px solid #080b1e' }}
                          >
                            <span style={{ color: src.color, fontWeight: 700 }}>{src.icon} {(w * 100).toFixed(0)}%</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="retrieval-sources-full">
                    {allSources.map((src) => {
                      const data = sources[src.key] || {};
                      const tech = data.tech || {};
                      const docs = data.documents || [];
                      const active = src.key === 'knowledge_graph' ? true : isHybrid;

                      return (
                        <div key={src.key} className={`retrieval-source-full ${!active ? 'retrieval-source-inactive' : ''}`}>
                          <div className="retrieval-source-full-header" style={{ borderLeftColor: active ? src.color : '#3a3f55' }}>
                            <div className="retrieval-source-full-title">
                              <span style={{ fontSize: '1.3rem' }}>{src.icon}</span>
                              <div>
                                <div style={{ color: active ? src.color : '#5a6384', fontWeight: 600, fontSize: '0.9rem' }}>{src.name}</div>
                                <div style={{ color: '#6a7394', fontSize: '0.75rem' }}>
                                  {active ? `${data.count || 0} ${src.key === 'knowledge_graph' ? 'nodes' : 'documents'} retrieved` : 'Not active in this query'}
                                </div>
                              </div>
                            </div>
                            {active && weights[src.key] && (
                              <span className="source-expanded-badge" style={{ borderColor: src.color + '40', color: src.color }}>
                                {(weights[src.key] * 100).toFixed(0)}% weight
                              </span>
                            )}
                          </div>

                          {/* How It Works */}
                          <div className="retrieval-source-how">
                            <div className="retrieval-how-label">How it works:</div>
                            <ol className="retrieval-how-steps">
                              {src.howItWorks.map((step, i) => (
                                <li key={i}>{step}</li>
                              ))}
                            </ol>
                            <div className="retrieval-why-matters">
                              <strong>Why it matters:</strong> {src.whyItMatters}
                            </div>
                          </div>

                          {/* Tech Config */}
                          {active && Object.keys(tech).length > 0 && (
                            <div className="retrieval-source-tech">
                              <div className="retrieval-tech-label">Configuration:</div>
                              <div className="source-expanded-tech-grid">
                                {Object.entries(tech).map(([k, v]) => (
                                  <div key={k} className="source-expanded-tech-item">
                                    <span className="source-expanded-tech-key">{k.replace(/_/g, ' ')}</span>
                                    <span className="source-expanded-tech-value">{String(v)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Top Documents */}
                          {active && docs.length > 0 && (
                            <div className="retrieval-source-docs">
                              <div className="retrieval-tech-label">Top retrieved documents:</div>
                              {docs.slice(0, 5).map((doc, idx) => {
                                const content = typeof doc === 'string' ? doc : doc?.content || doc?.page_content || '';
                                const page = typeof doc === 'object' ? doc?.page : null;
                                const section = typeof doc === 'object' ? doc?.section : null;
                                const nodeType = typeof doc === 'object' ? doc?.node_type : null;
                                return (
                                  <div key={idx} className="source-expanded-doc" style={{ borderLeftColor: src.color + '60' }}>
                                    <div className="source-expanded-doc-header">
                                      <span className="source-expanded-doc-idx" style={{ background: src.color + '20', color: src.color }}>#{idx + 1}</span>
                                      {page && <span className="source-expanded-doc-page">Page {page}</span>}
                                      {nodeType && <span className="source-expanded-doc-type">{nodeType}</span>}
                                    </div>
                                    {section && <div className="source-expanded-doc-section">{section}</div>}
                                    <div className="source-expanded-doc-content">{content}</div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* KG Graph Traversal inline */}
                          {src.key === 'knowledge_graph' && active && kgNodes > 0 && (
                            <div className="retrieval-source-graph">
                              <div className="retrieval-tech-label">Graph traversal path:</div>
                              <div className="retrieval-graph-path">
                                {currentReasoningPath?.map((node, i) => (
                                  <span key={i} className="retrieval-graph-node">
                                    <span className="retrieval-graph-node-type" style={{
                                      color: node.node_type === 'Vehicle' ? '#9b59b6' :
                                             node.node_type === 'System' ? '#3498db' :
                                             node.node_type === 'Component' ? '#66B4C4' :
                                             node.node_type === 'Specification' ? '#00ff88' :
                                             node.node_type === 'Warning' ? '#ff6b6b' : '#8892b0'
                                    }}>
                                      {node.node_type}
                                    </span>
                                    <span className="retrieval-graph-node-name">{node.node_data?.name || '—'}</span>
                                    {node.relationship && i < currentReasoningPath.length - 1 && (
                                      <span className="retrieval-graph-edge">→ <em>{currentReasoningPath[i+1]?.relationship || 'CONNECTED'}</em> →</span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Fusion & Ranking */}
                  {isHybrid && (
                    <div className="retrieval-fusion-box" style={{ marginTop: 14 }}>
                      <div className="retrieval-fusion-method">
                        <span className="retrieval-parsed-label">Fusion Method</span>
                        <span>Reciprocal Rank Fusion (RRF)</span>
                      </div>
                      <div className="retrieval-fusion-explain">
                        Each retriever produces a ranked list. RRF combines them by assigning each document a score of <code>1 / (k + rank)</code> where k=60.
                        Documents appearing in multiple retrievers get boosted scores. The final ranked list is passed to the LLM as context.
                      </div>
                    </div>
                  )}
                </div>

                {/* Final Answer Stats */}
                <div className="retrieval-expanded-section">
                  <h4>3. Answer Generation</h4>
                  <div className="retrieval-answer-stats">
                    <div className="retrieval-stat-card">
                      <div className="retrieval-stat-value">{totalDocs || kgNodes}</div>
                      <div className="retrieval-stat-label">Sources combined</div>
                    </div>
                    <div className="retrieval-stat-card">
                      <div className="retrieval-stat-value">{Math.round(confidence * 100)}%</div>
                      <div className="retrieval-stat-label">Confidence</div>
                    </div>
                    <div className="retrieval-stat-card">
                      <div className="retrieval-stat-value">{citations.length}</div>
                      <div className="retrieval-stat-label">Pages cited</div>
                    </div>
                    <div className="retrieval-stat-card">
                      <div className="retrieval-stat-value">{kgNodes}</div>
                      <div className="retrieval-stat-label">Graph nodes</div>
                    </div>
                  </div>
                  <div className="retrieval-model-info">
                    Final answer generated by <strong>Ollama {LLM_MODEL}</strong> (local, offline) using the combined retrieval context above.
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Expanded Source Overlay — Full view of a retrieval source */}
      {expandedSource && currentReasoningMsg && (() => {
        const isHybrid = currentReasoningMsg?.isHybrid;
        const sources = currentReasoningMsg?.retrievalDetails?.sources || {};
        const weights = currentReasoningMsg?.retrievalDetails?.ensemble_weights || {};
        const srcData = sources[expandedSource] || {};
        const docs = srcData.documents || [];
        const tech = srcData.tech || {};
        const nameMap = {
          vector_store: { icon: '🔍', name: 'ChromaDB — Semantic Search', color: '#66B4C4' },
          bm25: { icon: '📝', name: 'BM25 — Keyword Match', color: '#ffaa32' },
          knowledge_graph: { icon: '🕸️', name: 'NetworkX — Graph Traversal', color: '#a050ff' },
        };
        const meta = nameMap[expandedSource] || { icon: '📦', name: expandedSource, color: '#8892b0' };

        return (
          <div className="source-expanded-overlay" onClick={() => setExpandedSource(null)}>
            <div className="source-expanded-container" onClick={(e) => e.stopPropagation()}>
              <div className="source-expanded-header" style={{ borderBottomColor: meta.color + '30' }}>
                <div className="source-expanded-title">
                  <span style={{ fontSize: '1.4rem' }}>{meta.icon}</span>
                  <h2 style={{ color: meta.color }}>{meta.name}</h2>
                  {srcData.count != null && (
                    <span className="source-expanded-badge">{srcData.count} {expandedSource === 'knowledge_graph' ? 'nodes' : 'documents'}</span>
                  )}
                  {weights[expandedSource] != null && (
                    <span className="source-expanded-badge">{(weights[expandedSource] * 100).toFixed(0)}% weight</span>
                  )}
                </div>
                <button className="source-expanded-close" onClick={() => setExpandedSource(null)}>✕</button>
              </div>

              <div className="source-expanded-body">
                {/* Tech Details */}
                {Object.keys(tech).length > 0 && (
                  <div className="source-expanded-section">
                    <h4>Technical Configuration</h4>
                    <div className="source-expanded-tech-grid">
                      {Object.entries(tech).map(([k, v]) => (
                        <div key={k} className="source-expanded-tech-item">
                          <span className="source-expanded-tech-key">{k.replace(/_/g, ' ')}</span>
                          <span className="source-expanded-tech-value">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Retrieved Documents */}
                {docs.length > 0 && (
                  <div className="source-expanded-section">
                    <h4>All Retrieved Documents ({docs.length})</h4>
                    <div className="source-expanded-docs">
                      {docs.map((doc, idx) => {
                        const content = typeof doc === 'string' ? doc : doc?.content || doc?.page_content || doc?.snippet || doc?.text || '';
                        const page = typeof doc === 'object' ? doc?.page : null;
                        const section = typeof doc === 'object' ? doc?.section : null;
                        const nodeType = typeof doc === 'object' ? doc?.node_type : null;
                        return (
                          <div key={idx} className="source-expanded-doc" style={{ borderLeftColor: meta.color + '60' }}>
                            <div className="source-expanded-doc-header">
                              <span className="source-expanded-doc-idx" style={{ background: meta.color + '20', color: meta.color }}>#{idx + 1}</span>
                              {page && <span className="source-expanded-doc-page">Page {page}</span>}
                              {nodeType && <span className="source-expanded-doc-type">{nodeType}</span>}
                            </div>
                            {section && <div className="source-expanded-doc-section">{section}</div>}
                            <div className="source-expanded-doc-content">{content}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* KG specific */}
                {expandedSource === 'knowledge_graph' && srcData.reasoning_nodes > 0 && (
                  <div className="source-expanded-section">
                    <h4>Graph Reasoning</h4>
                    <p style={{ color: '#a0aac0', fontSize: '0.85rem' }}>
                      Traversed {srcData.reasoning_nodes} reasoning nodes across the knowledge graph using multi-hop traversal.
                      Click "Expand" on Stage 3 in the side panel to see the full graph visualization.
                    </p>
                  </div>
                )}

                {/* Query Translation */}
                {currentReasoningMsg?.retrievalDetails?.query_translations && (
                  <div className="source-expanded-section">
                    <h4>Query Translation</h4>
                    <div className="source-expanded-tech-grid">
                      <div className="source-expanded-tech-item">
                        <span className="source-expanded-tech-key">Original query</span>
                        <span className="source-expanded-tech-value">"{currentReasoningMsg.retrievalDetails.query_translations.original}"</span>
                      </div>
                      <div className="source-expanded-tech-item">
                        <span className="source-expanded-tech-key">Detected intent</span>
                        <span className="source-expanded-tech-value">{currentReasoningMsg.retrievalDetails.query_translations.intent}</span>
                      </div>
                      {currentReasoningMsg.retrievalDetails.query_translations.cypher && (
                        <div className="source-expanded-tech-item">
                          <span className="source-expanded-tech-key">Graph query</span>
                          <span className="source-expanded-tech-value" style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>
                            {currentReasoningMsg.retrievalDetails.query_translations.cypher}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Expanded Graph Overlay — Full screen KG view */}
      {expandedGraph && currentReasoningPath && (
        <div className="kg-expanded-overlay" onClick={() => setExpandedGraph(false)}>
          <div className="kg-expanded-container" onClick={(e) => e.stopPropagation()}>
            <div className="kg-expanded-header">
              <h2>🕸️ Knowledge Graph Traversal — {currentReasoningPath.length} nodes</h2>
              <button className="kg-expanded-close" onClick={() => setExpandedGraph(false)}>✕</button>
            </div>
            <div className="kg-expanded-body">
              <KnowledgeGraphReasoning
                reasoningPath={currentReasoningPath}
                isVisible={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* Voice Call Interface */}
      <VoiceCallInterface
        isOpen={showVoiceCall}
        onClose={() => { setShowVoiceCall(false); setPrearmedAudio(null); }}
        wssEndpoint={import.meta.env.VITE_WSS_ENDPOINT || "ws://localhost:5001/ws"}
        prearmedAudio={prearmedAudio}
      />

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div className="image-zoom-modal" onClick={() => setZoomedImage(null)}>
          <div className="image-zoom-content" onClick={(e) => e.stopPropagation()}>
            <div className="image-zoom-header">
              <div className="image-zoom-info">
                <h3>Page {zoomedImage.page}</h3>
                {zoomedImage.description && (
                  <p>{zoomedImage.description}</p>
                )}
                {allImages.length > 1 && (
                  <span className="image-counter">
                    Image {zoomedImageIndex + 1} of {allImages.length}
                  </span>
                )}
              </div>
              <div className="image-zoom-actions">
                <button
                  className="cv-analyze-btn"
                  onClick={() => handleCVAnalysis(zoomedImage)}
                  disabled={cvAnalysisLoading}
                  title="Analyze image with computer vision"
                >
                  {cvAnalysisLoading ? '⏳ Analyzing...' : '🔍 Analyze Image'}
                </button>
                <button className="image-zoom-close" onClick={() => {
                  setZoomedImage(null);
                  setShowCvAnalysis(false);
                  setCvAnalysis(null);
                }}>
                  ✕
                </button>
              </div>
            </div>
            <div className="image-zoom-body">
              {/* Previous Button */}
              {allImages.length > 1 && zoomedImageIndex > 0 && (
                <button
                  className="image-nav-btn image-nav-prev"
                  onClick={() => {
                    const newIndex = zoomedImageIndex - 1;
                    setZoomedImageIndex(newIndex);
                    setZoomedImage(allImages[newIndex]);
                  }}
                  title="Previous image (←)"
                >
                  ‹
                </button>
              )}

              <img
                src={`${API_BASE}/${zoomedImage.file_path}`}
                alt={zoomedImage.description || `Image from page ${zoomedImage.page}`}
                className="zoomed-image"
              />

              {/* Next Button */}
              {allImages.length > 1 && zoomedImageIndex < allImages.length - 1 && (
                <button
                  className="image-nav-btn image-nav-next"
                  onClick={() => {
                    const newIndex = zoomedImageIndex + 1;
                    setZoomedImageIndex(newIndex);
                    setZoomedImage(allImages[newIndex]);
                  }}
                  title="Next image (→)"
                >
                  ›
                </button>
              )}
            </div>
            <div className="image-zoom-footer">
              <p>
                {allImages.length > 1
                  ? 'Use ← → arrows or click buttons to navigate | ESC to close'
                  : 'Click outside or press ESC to close'}
              </p>
            </div>

            {/* CV Analysis Results Panel */}
            {showCvAnalysis && (
              <div className="cv-analysis-panel">
                <div className="cv-analysis-header">
                  <h4>🔬 Computer Vision Analysis</h4>
                  <button
                    className="cv-analysis-close"
                    onClick={() => {
                      setShowCvAnalysis(false);
                      setCvAnalysis(null);
                    }}
                    title="Close analysis"
                  >
                    ✕
                  </button>
                </div>
                <div className="cv-analysis-body">
                  {cvAnalysisLoading && (
                    <div className="cv-analysis-loading">
                      <div className="spinner"></div>
                      <p>Analyzing image with AI vision and OCR...</p>
                    </div>
                  )}

                  {cvAnalysis?.error && (
                    <div className="cv-analysis-error">
                      ❌ {cvAnalysis.error}
                    </div>
                  )}

                  {cvAnalysis && !cvAnalysis.error && (
                    <div className="cv-analysis-results">
                      {/* AI Vision Description */}
                      {cvAnalysis.analyses?.vision?.description && (
                        <div className="cv-section cv-vision">
                          <h5>🤖 AI Vision Analysis</h5>
                          <p className="cv-description">{cvAnalysis.analyses.vision.description}</p>
                          {cvAnalysis.analyses.vision.detected_content && (
                            <div className="cv-badges">
                              {cvAnalysis.analyses.vision.detected_content.has_components && (
                                <span className="cv-badge">📦 Components</span>
                              )}
                              {cvAnalysis.analyses.vision.detected_content.has_diagram && (
                                <span className="cv-badge">📊 Diagram</span>
                              )}
                              {cvAnalysis.analyses.vision.detected_content.has_specifications && (
                                <span className="cv-badge">📏 Specs</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* OCR Text Extraction */}
                      {cvAnalysis.analyses?.ocr?.text && cvAnalysis.analyses.ocr.text.length > 10 && (
                        <div className="cv-section cv-ocr">
                          <h5>📝 Extracted Text ({cvAnalysis.analyses.ocr.word_count} words)</h5>
                          <div className="cv-text-box">
                            {cvAnalysis.analyses.ocr.text}
                          </div>
                          {cvAnalysis.analyses.ocr.content_type && (
                            <div className="cv-badges">
                              {cvAnalysis.analyses.ocr.content_type.likely_table && (
                                <span className="cv-badge">📋 Table</span>
                              )}
                              {cvAnalysis.analyses.ocr.content_type.likely_diagram && (
                                <span className="cv-badge">🔧 Diagram</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Technical Analysis */}
                      {cvAnalysis.analyses?.technical?.is_technical && (
                        <div className="cv-section cv-technical">
                          <h5>⚙️ Technical Content Detected</h5>
                          {cvAnalysis.analyses.technical.categories && (
                            <div className="cv-categories">
                              <strong>Categories:</strong>{' '}
                              {cvAnalysis.analyses.technical.categories.join(', ')}
                            </div>
                          )}
                          {cvAnalysis.analyses.technical.detected_elements && cvAnalysis.analyses.technical.detected_elements.length > 0 && (
                            <div className="cv-keywords">
                              <strong>Keywords:</strong>{' '}
                              {cvAnalysis.analyses.technical.detected_elements.slice(0, 10).join(', ')}
                              {cvAnalysis.analyses.technical.detected_elements.length > 10 && ` (+${cvAnalysis.analyses.technical.detected_elements.length - 10} more)`}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Image Info */}
                      {cvAnalysis.image_info && (
                        <div className="cv-section cv-info">
                          <h5>ℹ️ Image Information</h5>
                          <div className="cv-info-grid">
                            <span>Size: {cvAnalysis.image_info.size[0]} × {cvAnalysis.image_info.size[1]}px</span>
                            <span>Format: {cvAnalysis.image_info.format}</span>
                            <span>Mode: {cvAnalysis.image_info.mode}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
